import { useState, useEffect } from "react";
import { HelpCircle, ChevronLeft, Phone, X, DoorOpen, Key, Wrench, ArrowUpDown, Shield, AlertTriangle, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CallbackForm } from "@/components/CallbackForm";

const services = [
  { id: "ouverture", label: "Ouverture de porte", icon: DoorOpen, advice: "Ne forcez pas la serrure. Vérifiez si une autre entrée est accessible." },
  { id: "changement", label: "Changement de serrure", icon: Key, advice: "Notez la marque et le modèle de votre serrure actuelle si possible." },
  { id: "reparation", label: "Réparation de serrure", icon: Wrench, advice: "Évitez d'utiliser la serrure endommagée pour ne pas aggraver le problème." },
  { id: "volet", label: "Volet roulant", icon: ArrowUpDown, advice: "Ne forcez pas le mécanisme. Vérifiez si la sangle ou la manivelle est bloquée." },
  { id: "blindee", label: "Porte blindée", icon: Shield, advice: "Vérifiez que le problème ne vient pas de la clé elle-même." },
  { id: "urgence", label: "Dépannage urgence", icon: AlertTriangle, advice: "Mettez-vous en sécurité. Ne tentez pas de réparer vous-même." },
];

type Step = "services" | "advice" | "urgency" | "urgent" | "callback";

const HelpButton = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<Step>("services");
  const [selectedService, setSelectedService] = useState<typeof services[0] | null>(null);
  const [callbackOpen, setCallbackOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsVisible(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const reset = () => {
    setStep("services");
    setSelectedService(null);
    setIsOpen(false);
  };

  const selectService = (service: typeof services[0]) => {
    setSelectedService(service);
    setStep("advice");
  };

  const goBack = () => {
    if (step === "advice") setStep("services");
    else if (step === "urgency") setStep("advice");
    else if (step === "urgent" || step === "callback") setStep("urgency");
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => { setIsOpen(!isOpen); if (!isOpen) { setStep("services"); setSelectedService(null); } }}
        className={`fixed left-4 bottom-4 z-40 flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-3 shadow-lg hover:shadow-xl transition-all duration-300 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10 pointer-events-none"
        }`}
        aria-label="Besoin d'aide ?"
      >
        <HelpCircle className="h-5 w-5" />
        <span className="text-sm font-medium hidden sm:inline">Besoin d'aide ?</span>
      </button>

      {/* Panel */}
      {isOpen && isVisible && (
        <div className="fixed left-4 bottom-20 z-50 w-80 max-w-[calc(100vw-2rem)] bg-background border rounded-2xl shadow-xl animate-in slide-in-from-bottom-4 fade-in duration-200">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            {step !== "services" && (
              <button onClick={goBack} className="p-1 hover:bg-accent rounded-md transition-colors">
                <ChevronLeft className="h-5 w-5" />
              </button>
            )}
            <h3 className="font-semibold text-sm flex-1 text-center">
              {step === "services" && "Quel est votre besoin ?"}
              {step === "advice" && "Nos conseils"}
              {step === "urgency" && "Est-ce urgent ?"}
              {step === "urgent" && "Appelez-nous !"}
              {step === "callback" && "Être rappelé"}
            </h3>
            <button onClick={reset} className="p-1 hover:bg-accent rounded-md transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-2">
            {step === "services" && services.map((s) => (
              <button
                key={s.id}
                onClick={() => selectService(s)}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-accent transition-colors text-left text-sm"
              >
                <s.icon className="h-5 w-5 text-primary shrink-0" />
                <span>{s.label}</span>
              </button>
            ))}

            {step === "advice" && selectedService && (
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 bg-accent/50 rounded-xl">
                  <selectedService.icon className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">{selectedService.label}</p>
                    <p className="text-sm text-muted-foreground mt-1">{selectedService.advice}</p>
                  </div>
                </div>
                <Button onClick={() => setStep("urgency")} className="w-full">Continuer</Button>
              </div>
            )}

            {step === "urgency" && (
              <div className="space-y-3">
                <Button variant="destructive" onClick={() => setStep("urgent")} className="w-full gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  C'est urgent !
                </Button>
                <Button variant="outline" onClick={() => setStep("callback")} className="w-full gap-2">
                  <Calendar className="h-4 w-4" />
                  Ça peut attendre
                </Button>
              </div>
            )}

            {step === "urgent" && (
              <div className="space-y-4 text-center">
                <p className="text-sm text-muted-foreground">Nous intervenons <strong>24h/24</strong> et <strong>7j/7</strong></p>
                <a href="tel:0766009189" className="block">
                  <Button className="w-full gap-2" size="lg">
                    <Phone className="h-5 w-5" />
                    07 66 00 91 89
                  </Button>
                </a>
              </div>
            )}

            {step === "callback" && (
              <div className="space-y-4 text-center">
                <p className="text-sm text-muted-foreground">Demandez à être rappelé à l'heure qui vous convient</p>
                <Button onClick={() => { setCallbackOpen(true); reset(); }} className="w-full gap-2">
                  <Calendar className="h-4 w-4" />
                  Planifier un rappel
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      <CallbackForm open={callbackOpen} onOpenChange={setCallbackOpen} />
    </>
  );
};

export default HelpButton;
